import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import GetAppIcon from '@material-ui/icons/GetApp';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { useAttachmentDialog } from 'features/shared/hooks/useAttachmentDialog';
import { ATTACHMENT_PARENT_TYPES } from 'features/shared/constants/attachmentParentTypes';
import {
  getAttachments,
  getAttachment,
  deleteAttachment,
} from '../../../shared/services/attachmentService';
import { ATTACHMENT_GRID_COLUMNS } from '../../constants/vehicleAttachmentsTabConstants';
import FormSpinner from 'components/FormSpinner';

const VehicleAttachmentsTab = ({ ptsVehicleID, canEdit = true }) => {
  const dispatch = useDispatch();
  const permissions = usePermissions(PERMISSION_RESOURCES.VEHICLE);
  const canEditVehicle = canEdit && permissions.Edit;
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const { openAttachmentDialog } = useAttachmentDialog();
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadAttachments = useCallback(async () => {
    if (!ptsVehicleID) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getAttachments({ id: ptsVehicleID, type: ATTACHMENT_PARENT_TYPES.VEHICLE }),
      onSuccess: (data) => setAttachments(data),
    });
  }, [dispatch, ptsVehicleID]);

  const handleAdd = useCallback(() => {
    openAttachmentDialog({
      parentId: ptsVehicleID,
      parentType: ATTACHMENT_PARENT_TYPES.VEHICLE,
      canSave: canEditVehicle,
      onSaved: loadAttachments,
    });
  }, [openAttachmentDialog, ptsVehicleID, canEditVehicle, loadAttachments]);

  const handleEdit = useCallback(
    (attachment) => {
      openAttachmentDialog({
        parentId: ptsVehicleID,
        parentType: ATTACHMENT_PARENT_TYPES.VEHICLE,
        canSave: canEditVehicle,
        attachment,
        onSaved: loadAttachments,
      });
    },
    [openAttachmentDialog, ptsVehicleID, canEditVehicle, loadAttachments]
  );

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${
          row.FileName || 'attachment'
        }" record?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () => deleteAttachment(row.ptsAttachmentID),
            onSuccess: () => {
              dispatch(notify('Attachment deleted successfully', 'success'));
              loadAttachments();
            },
          });
        },
        'Delete Attachment'
      );
    },
    [dispatch, requestConfirm, loadAttachments]
  );

  const handleDownload = useCallback(
    async (row) => {
      await executeApiAction({
        setLoading,
        dispatch,
        action: async () => {
          const response = await getAttachment(row.ptsAttachmentID);
          const blob = new Blob([response.buffer], { type: response.type });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');

          link.href = url;
          link.download = row.FileName;
          link.click();

          window.URL.revokeObjectURL(url);
        },
      });
    },
    [dispatch]
  );

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  return (
    <Box className="vehicle-grid-tab">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Attachment"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditVehicle}
        />
      </Box>

      <CommonDataGrid
        rows={attachments}
        columns={ATTACHMENT_GRID_COLUMNS}
        getRowId={(row) => row.ptsAttachmentID}
        toolbar={getDataGridToolbarProps({ printTitle: 'Vehicle Attachments' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditVehicle },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditVehicle },
          custom: [
            {
              icon: <GetAppIcon />,
              tooltip: 'Download',
              onClick: handleDownload,
            },
          ],
        }}
      />

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default VehicleAttachmentsTab;
